import { Injectable } from '@angular/core';
import { NAVIGATION } from '@constants/navigation';

// Imports Core & Auth d'Amplify V6 (Fini le UI service pour la logique métier)
import { fetchAuthSession, getCurrentUser, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

import { BehaviorSubject, from, of, catchError, map, switchMap, shareReplay, distinctUntilChanged } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // Le BehaviorSubject privé permet de contrôler l'état en interne
  private _authenticated$ = new BehaviorSubject<boolean>(false);

  // Observable public, avec distinctUntilChanged pour éviter les émissions doublons
  authenticated$ = this._authenticated$.asObservable().pipe(distinctUntilChanged());

  constructor() {
    // 1. Initialisation : On vérifie l'état réel au chargement de l'application
    this.checkInitialAuthState();

    // 2. Réactivité : On écoute les événements système d'Amplify en temps réel
    Hub.listen('auth', data => {
      switch (data.payload.event) {
        case 'signedIn':
          this._authenticated$.next(true);
          break;
        case 'signedOut':
        case 'tokenRefresh_failure':
          this._authenticated$.next(false);
          break;
      }
    });
  }

  // Vérifie l'état de connexion au démarrage (silencieusement)
  private async checkInitialAuthState() {
    try {
      await getCurrentUser();
      this._authenticated$.next(true);
    } catch (error) {
      this._authenticated$.next(false);
    }
  }

  // Permet de lire la valeur de manière synchrone (utile dans les Guards)
  isAuthenticated(): boolean {
    return this._authenticated$.value;
  }

  async signOut() {
    await signOut();
  }

  async getToken(): Promise<string | undefined> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString();
    } catch {
      return undefined;
    }
  }

  async isValidSession(): Promise<boolean> {
    try {
      const session = await fetchAuthSession();
      return session.tokens !== undefined;
    } catch {
      return false;
    }
  }

  // ========================================================================
  // FLUX RÉACTIFS (RxJS) OPTIMISÉS
  // ========================================================================

  getGroups$ = this.authenticated$.pipe(
    switchMap(isAuthenticated => {
      if (!isAuthenticated) return of([] as string[]);

      return from(fetchAuthSession()).pipe(
        map(session => {
          const payload = session.tokens?.accessToken?.payload;
          const groups = payload?.['cognito:groups'] as string[] | undefined;
          return groups || [];
        }),
        catchError(err => {
          console.error('Erreur lors de la récupération des groupes Cognito', err);
          return of([] as string[]);
        })
      );
    }),
    // CRUCIAL : Partage le résultat de ce flux avec tous les abonnés (isAdmin$, navigation$)
    // Cela évite de rappeler `fetchAuthSession` plusieurs fois inutilement.
    shareReplay(1)
  );

  isAdmin$ = this.getGroups$.pipe(map(groups => groups.includes('admin')));

  navigation$ = this.getGroups$.pipe(
    map(groups => {
      return NAVIGATION.filter(nav => {
        if (nav.groups && nav.groups.length > 0) {
          return nav.groups.every(g => groups.includes(g));
        }
        return true;
      });
    }),
    catchError(err => {
      console.error('Erreur lors de la génération de la navigation', err);
      return of(NAVIGATION.filter(n => !n.groups));
    })
  );
}
